import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, superAdminAPI } from '../services/api';

const SA_TOKEN_KEY = 'sa_token';
const SA_USER_KEY = 'sa_user';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [features, setFeatures] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const fetchFeatures = async () => {
    try {
      const response = await authAPI.getFeatures();
      setFeatures(response.data.features);
      setSubscription(response.data.subscription ?? null);
    } catch (error) {
      console.error('Failed to load feature flags:', error);
    }
  };

  // Load user from localStorage on mount, then refresh from the server so
  // edits made elsewhere (e.g. a principal updating the parent's name) show
  // up without forcing a logout/login.
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');

    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
      fetchFeatures();

      authAPI.getMe()
        .then((res) => {
          const freshUser = res.data.user;
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
        })
        .catch(() => {});
    }

    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { token, user } = response.data;

      // Save to localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      setToken(token);
      setUser(user);
      fetchFeatures();

      return { success: true, user };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear localStorage and state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      setFeatures(null);
      setSubscription(null);
    }
  };

  // A feature with no entry yet (still loading, or no flags fetched) defaults
  // to enabled — same "missing row = enabled" rule the backend uses.
  const isFeatureEnabled = (key) => {
    if (!features) return true;
    return features[key] !== false;
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') {
      return user.role === roles;
    }
    return roles.includes(user.role);
  };

  const isImpersonating = () => {
    return !!localStorage.getItem(SA_TOKEN_KEY);
  };

  const startImpersonation = async (schoolId) => {
    if (isImpersonating()) {
      return { success: false, error: 'Already in an impersonation session. Exit first.' };
    }

    try {
      const response = await superAdminAPI.startImpersonation(schoolId);
      const { token: impToken, user: principalUser } = response.data;

      // Save the current super admin session before swapping
      localStorage.setItem(SA_TOKEN_KEY, localStorage.getItem('token'));
      localStorage.setItem(SA_USER_KEY, localStorage.getItem('user'));

      localStorage.setItem('token', impToken);
      localStorage.setItem('user', JSON.stringify(principalUser));
      setToken(impToken);
      setUser(principalUser);

      await fetchFeatures();

      return { success: true, user: principalUser };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to start impersonation';
      return { success: false, error: message };
    }
  };

  const exitImpersonation = async () => {
    try {
      await superAdminAPI.exitImpersonation();
    } catch (error) {
      console.error('exitImpersonation backend call failed (non-fatal):', error.message);
    }

    const saToken = localStorage.getItem(SA_TOKEN_KEY);
    const saUser = localStorage.getItem(SA_USER_KEY);

    if (saToken && saUser) {
      localStorage.setItem('token', saToken);
      localStorage.setItem('user', saUser);
      localStorage.removeItem(SA_TOKEN_KEY);
      localStorage.removeItem(SA_USER_KEY);

      const restoredUser = JSON.parse(saUser);
      setToken(saToken);
      setUser(restoredUser);

      await fetchFeatures();
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      setFeatures(null);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated,
    hasRole,
    features,
    isFeatureEnabled,
    subscription,
    isImpersonating,
    startImpersonation,
    exitImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
